/**
 * Feature-detection helpers for the optional Android/iOS native wrapper bridge.
 *
 * `window.PlaintrMobilePushBridge` is injected only when the web app runs
 * inside a supported wrapper's WebView on the confirmed origin; in every
 * other environment (Chrome, Firefox, Safari, Edge, etc.) it is absent.
 * Callers must check availability before invoking any bridge method, so a
 * normal browser session is unaffected by this module's existence.
 *
 * SECURITY: this module never reads, stores, or logs the bridge's return
 * values — that responsibility belongs to each caller, which must never log
 * an intentToken, installationId, installationSecret, or FCM token.
 */

/**
 * @returns {null | {
 *   requestPushPermission: Function,
 *   associateAuthenticatedInstallation: Function,
 *   requestLogout: Function,
 * }} the bridge object, or `null` when it isn't present.
 */
export function getNativeBridge() {
  if (typeof window === "undefined") {
    return null;
  }

  const bridge = window.PlaintrMobilePushBridge;
  if (!bridge || typeof bridge !== "object") {
    return null;
  }

  return bridge;
}

/**
 * Resolve the wrapper platform through its synchronous native bridge.
 * Older Android releases predate getPlatform; require their actual Capacitor
 * Android transport before using the legacy fallback. Unknown/malformed
 * platforms fail closed. This identifies the platform, not push readiness.
 * @returns {"android" | "ios" | null}
 */
export function getNativePlatform() {
  const bridge = getNativeBridge();
  if (!bridge) return null;

  try {
    if (!("getPlatform" in bridge)) {
      return typeof bridge.associateAuthenticatedInstallation === "function" &&
        typeof window.androidBridge?.postMessage === "function"
        ? "android"
        : null;
    }
    if (typeof bridge.getPlatform !== "function") return null;
    const platform = bridge.getPlatform();
    return platform === "android" || platform === "ios" ? platform : null;
  } catch {
    return null;
  }
}

/**
 * True only when the native bridge is present and exposes the
 * `associateAuthenticatedInstallation` entry point as a function.
 */
export function isNativeBridgeAvailable() {
  const bridge = getNativeBridge();
  return (
    !!bridge && typeof bridge.associateAuthenticatedInstallation === "function"
  );
}

/**
 * True only when the native bridge is present and exposes the
 * `requestLogout` entry point as a function.
 */
export function isNativeLogoutAvailable() {
  const bridge = getNativeBridge();
  return !!bridge && typeof bridge.requestLogout === "function";
}

/**
 * True only when the native bridge is present and exposes the
 * `requestSocialLogin` entry point as a function.
 */
export function isSocialLoginBridgeAvailable() {
  const bridge = getNativeBridge();
  return !!bridge && typeof bridge.requestSocialLogin === "function";
}

const KNOWN_SOCIAL_PROVIDERS = ["google", "apple"];

/**
 * True only when the bridge is present AND exposes
 * `getSocialLoginCapabilities` as a function. Distinguishes a legacy native
 * wrapper (bridge present, method absent — pre-capabilities behavior) from a
 * wrapper that implements the method but returns invalid data (fail closed).
 */
export function isSocialLoginCapabilitiesSupported() {
  const bridge = getNativeBridge();
  return !!bridge && typeof bridge.getSocialLoginCapabilities === "function";
}

/**
 * @returns {null | { providers: string[] }} the wrapper's supported
 * social-login providers, or `null` if unavailable, malformed, or the
 * bridge call throws. Synchronous; never mutates the bridge's result.
 */
export function getSocialLoginCapabilities() {
  const bridge = getNativeBridge();
  if (!bridge || typeof bridge.getSocialLoginCapabilities !== "function") {
    return null;
  }

  let result;
  try {
    result = bridge.getSocialLoginCapabilities();
  } catch (error) {
    return null;
  }

  if (
    !result ||
    typeof result !== "object" ||
    !Array.isArray(result.providers)
  ) {
    return null;
  }

  return {
    providers: result.providers.filter((provider) =>
      KNOWN_SOCIAL_PROVIDERS.includes(provider)
    ),
  };
}
